import React, { useMemo, useState, useEffect } from 'react';
import {
    Database, AlertCircle, Unlink, X, Type, Image, ArrowLeft, CheckCircle2, RefreshCw,
    ChevronRight, ChevronDown, Braces, Eye, EyeOff, GripVertical, Plus, Layers, Sparkles,
    Zap, Globe
} from 'lucide-react';
import { useGetCustomApisQuery } from '../../features/integrations/customApiApi';
import { useGetWebhooksQuery, WebhookData } from '../../features/integrations/webhookApi';
import { toast } from 'react-hot-toast';
import { getFlattenedKeys, findFuzzyMatch } from '../../pages/template-editor/utils/autoMapper';
import { resolveJsonPath } from '../../pages/template-editor/utils/bindingResolver';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../../components/ui/select';
import { Layer } from '../../types/design';

interface BindableLayerInfo {
    id: string;
    name: string;
    type: string;
    binding?: {
        sourceType?: 'custom_api' | 'webhook';
        customApiId?: string;
        webhookId?: string;
        apiKey: string;
        fallbackValue?: string;
    };
}

interface DataBindingSidepaneProps {
    layers: Layer[];
    textStyles: Record<string, any>;
    imageSettings: Record<string, any>;
    selectedLayerId: string | null;
    onSelectLayer: (id: string | null) => void;
    category?: string;
    onBindingChange: (layerId: string, binding: any | null) => void;
    onAutoMapAll?: (apiId: string, activeLayerBindingOverride?: any) => void;
    onAddBoundLayer?: (
        apiId: string,
        apiKey: string,
        value: string,
        dropX?: number,
        dropY?: number,
        customWidth?: number,
        customHeight?: number,
        ignoreBounds?: boolean,
        isBulkDrop?: boolean,
        sourceType?: string,
        webhookId?: string
    ) => void;
    onClose: () => void;
    showPlaceholders: boolean;
    onShowPlaceholdersChange: (val: boolean) => void;
    autoPaginate?: boolean;
    onAutoPaginateChange?: (val: boolean) => void;
    templateSettings: any;
    onSettingsChange?: (updates: any) => void;
}

const TECHNICAL_KEYS = new Set([
    'headers', 'config', 'request', 'response',
    'headers_list', 'header_values', 'request_body', 'api_url', 'data_json'
]);

// --- Recursive JSON Node Component ---
interface JSONNodeProps {
    name: string;
    value: any;
    path: string;
    depth: number;
    selectedApiKey: string;
    onSelectKey: (key: string) => void;
    onDragStart: (e: React.DragEvent, path: string, val: any) => void;
    onAdd?: (path: string, val: any) => void;
    isWebhook?: boolean;
}

const JSONNode: React.FC<JSONNodeProps> = ({
    name,
    value,
    path,
    depth,
    selectedApiKey,
    onSelectKey,
    onDragStart,
    onAdd,
    isWebhook = false,
}) => {
    const [isOpen, setIsOpen] = useState(depth < 2);
    const isObject = value !== null && typeof value === 'object';
    const isArray = Array.isArray(value);
    const isSelected = selectedApiKey === path;

    useEffect(() => {
        if (depth < 2) {
            setIsOpen(true);
        }
    }, [value, depth]);

    const handleToggle = (e: React.MouseEvent) => {
        e.stopPropagation();
        setIsOpen(!isOpen);
    };

    const handleNodeDragStart = (e: React.DragEvent) => {
        onDragStart(e, path, value);
    };

    const renderValuePreview = (val: any) => {
        if (val === null) return <span className="text-gray-400 font-mono text-[10px] font-medium">null</span>;
        if (typeof val === 'boolean') return <span className="text-orange-500 font-mono text-[10px] font-medium">{String(val)}</span>;
        if (typeof val === 'number') return <span className="text-blue-500 font-mono text-[10px] font-medium">{val}</span>;

        const valStr = String(val);
        const isImg = valStr.startsWith('http') && /\.(jpg|jpeg|png|webp|gif|svg)/i.test(valStr.split('?')[0]);
        if (isImg) {
            return (
                <span className="text-teal-600 font-mono text-[10px] font-medium truncate flex items-center gap-1">
                    <span className="text-[9px] bg-teal-50 text-teal-600 border border-teal-200 px-1 py-0.2 rounded shrink-0">img</span>
                    "{valStr.length > 80 ? valStr.substring(0, 80) + '...' : valStr}"
                </span>
            );
        }
        return <span className="text-green-600 font-mono text-[10px] font-medium break-all">"{valStr.length > 80 ? valStr.substring(0, 80) + '...' : valStr}"</span>;
    };

    return (
        <div className="select-none font-sans text-xs">
            <div
                className={`group flex items-center justify-between py-1 px-1.5 rounded transition-all ${isSelected
                        ? 'bg-[#30A84B]/10 border border-[#30A84B]/20 text-[#30A84B]'
                        : 'hover:bg-gray-50 text-gray-700'
                    }`}
                style={{ paddingLeft: `${depth * 12 + 6}px` }}
            >
                <div
                    className="flex items-center gap-1.5 flex-1 min-w-0 cursor-pointer"
                    onClick={isObject ? handleToggle : () => onSelectKey(path)}
                    draggable="true"
                    onDragStart={handleNodeDragStart}
                >
                    {!isObject && (
                        <div className="opacity-0 group-hover:opacity-100 text-gray-450 cursor-grab shrink-0 transition-opacity">
                            <GripVertical className="w-3 h-3" />
                        </div>
                    )}

                    {isObject ? (
                        <div className="text-gray-400 shrink-0">
                            {isOpen ? <ChevronDown className="w-3.5 h-3.5" /> : <ChevronRight className="w-3.5 h-3.5" />}
                        </div>
                    ) : (
                        <div className="w-3.5 h-3.5 rounded bg-green-50 border border-green-200 flex items-center justify-center shrink-0">
                            <div className="w-1.5 h-1.5 rounded-full bg-[#30A84B]" />
                        </div>
                    )}

                    <span className={`font-mono text-[11px] truncate ${isObject ? 'text-purple-700 font-semibold' : 'text-gray-700 font-medium'}`}>
                        {name}
                    </span>

                    {isObject ? (
                        <span className="text-gray-400 font-mono text-[10px] ml-1">
                            {isArray ? `[${value.length}]` : '{...}'}
                        </span>
                    ) : (
                        <span className="text-gray-400 font-mono text-[10px]">:</span>
                    )}

                    {!isObject && (
                        <div className="flex-1 min-w-0 truncate text-[11px] ml-1">
                            {renderValuePreview(value)}
                        </div>
                    )}
                </div>

                {!isObject && onAdd && (
                    <button
                        type="button"
                        onClick={(e) => {
                            e.stopPropagation();
                            onAdd(path, value);
                        }}
                        className="opacity-0 group-hover:opacity-100 p-0.5 hover:bg-green-100 text-[#30A84B] rounded cursor-pointer transition-opacity shrink-0 ml-1.5"
                        title="Bind or Add Layer"
                    >
                        <Plus className="w-3.5 h-3.5" />
                    </button>
                )}
            </div>

            {isObject && isOpen && (
                <div className="relative border-l border-gray-100 ml-2.5 pl-0.5 mt-0.5">
                    {Object.entries(value)
                        .filter(([key, val]) => {
                            if (val === null || val === undefined) return false;
                            if (!isWebhook && depth === 0 && TECHNICAL_KEYS.has(key.toLowerCase())) return false;
                            return true;
                        })
                        .map(([key, val]) => {
                            const isArrayParent = Array.isArray(value);
                            const nodeName = isArrayParent ? `[${key}]` : key;
                            const childPath = isArrayParent
                                ? `${path}[${key}]`
                                : (path ? `${path}.${key}` : key);

                            return (
                                <JSONNode
                                    key={childPath}
                                    name={nodeName}
                                    value={val}
                                    path={childPath}
                                    depth={depth + 1}
                                    selectedApiKey={selectedApiKey}
                                    onSelectKey={onSelectKey}
                                    onDragStart={onDragStart}
                                    onAdd={onAdd}
                                    isWebhook={isWebhook}
                                />
                            );
                        })}
                </div>
            )}
        </div>
    );
};

const getArrayLength = (apiData: any): number => {
    if (!apiData) return 1;
    if (Array.isArray(apiData)) return apiData.length;
    if (typeof apiData === 'object') {
        for (const key of Object.keys(apiData)) {
            if (Array.isArray(apiData[key])) {
                return apiData[key].length;
            }
        }
    }
    return 1;
};

export const DataBindingSidepane: React.FC<DataBindingSidepaneProps> = ({
    layers,
    textStyles,
    imageSettings,
    selectedLayerId,
    onSelectLayer,
    category = 'general',
    onBindingChange,
    onAutoMapAll: _onAutoMapAll,
    onAddBoundLayer,
    onClose,
    showPlaceholders,
    onShowPlaceholdersChange,
    autoPaginate = false,
    onAutoPaginateChange,
    templateSettings,
    onSettingsChange,
}) => {
    // 1. Data Source Mode: 'custom_api' | 'webhook'
    const [sourceType, setSourceType] = useState<'custom_api' | 'webhook'>('custom_api');

    // 2. Query Custom APIs
    const { data: customApiRes, isLoading: isCustomApisLoading, refetch: refetchCustomApis, isFetching: isCustomApisFetching } = useGetCustomApisQuery(undefined, {
        pollingInterval: 60000,
    });
    const customApis = customApiRes?.data || [];

    // 3. Query Webhooks from CMS
    const { data: webhooksRes, isLoading: isWebhooksLoading, refetch: refetchWebhooks, isFetching: isWebhooksFetching } = useGetWebhooksQuery(undefined, {
        pollingInterval: 60000,
    });
    const webhooks = webhooksRes?.data || [];

    // Local storage-backed toggle state for auto-map
    const autoMapEnabled = React.useMemo(() => {
        return localStorage.getItem('wejha_auto_map_enabled') === 'true';
    }, []);

    // Local storage-backed toggle state for the help guide
    const [isGuideOpen, setIsGuideOpen] = React.useState<boolean>(() => {
        const saved = localStorage.getItem('wejha_binding_guide_open');
        return saved !== null ? saved === 'true' : true;
    });

    const handleToggleGuide = () => {
        const newVal = !isGuideOpen;
        setIsGuideOpen(newVal);
        localStorage.setItem('wejha_binding_guide_open', String(newVal));
    };

    // View Mode State
    const [viewMode, setViewMode] = useState<'structure' | 'json'>('structure');

    // Filter APIs: Show matching category + general APIs
    const filteredApis = useMemo(() => {
        if (!category || category === 'general') return customApis;
        const catLower = category.toLowerCase();
        if (['weather', 'prayer', 'forex'].includes(catLower)) {
            return customApis.filter(api => api.api_type === catLower || api.api_type === 'general');
        }
        return customApis;
    }, [customApis, category]);

    // Flatten all layers recursively to find bindable ones
    const bindableLayers = useMemo((): BindableLayerInfo[] => {
        const result: BindableLayerInfo[] = [];
        const traverse = (list: Layer[]) => {
            for (const layer of list) {
                if (layer.type === 'text' || layer.type === 'image') {
                    result.push({
                        id: layer.id,
                        name: layer.name,
                        type: layer.type,
                        binding: layer.type === 'text'
                            ? textStyles[layer.id]?.binding
                            : imageSettings[layer.id]?.binding,
                    });
                }
                if (layer.children && layer.children.length > 0) {
                    traverse(layer.children);
                }
            }
        };
        traverse(layers);
        return result;
    }, [layers, textStyles, imageSettings]);

    // Find the currently active selected layer
    const activeBindableLayer = useMemo(() => {
        if (!selectedLayerId) return null;
        return bindableLayers.find(l => l.id === selectedLayerId) || null;
    }, [bindableLayers, selectedLayerId]);

    // Current binding states
    const activeBinding = activeBindableLayer?.binding;
    const selectedApiId = activeBinding?.customApiId || '';
    const selectedWebhookIdFromBinding = (activeBinding?.sourceType === 'webhook' || activeBinding?.webhookId)
        ? (activeBinding.webhookId || activeBinding.customApiId || '')
        : '';
    const selectedApiKey = activeBinding?.apiKey || '';
    const fallbackValue = activeBinding?.fallbackValue || '';

    // Global selected IDs (when no layer is selected)
    const [globalApiId, setGlobalApiId] = React.useState<string>('');
    const [globalWebhookId, setGlobalWebhookId] = React.useState<string>('');

    // Automatically synchronize sourceType with selected layer's binding
    useEffect(() => {
        if (activeBinding) {
            if (activeBinding.sourceType === 'webhook' || Boolean(activeBinding.webhookId)) {
                setSourceType('webhook');
                if (activeBinding.webhookId || activeBinding.customApiId) {
                    setGlobalWebhookId(activeBinding.webhookId || activeBinding.customApiId || '');
                }
            } else if (activeBinding.sourceType === 'custom_api' || Boolean(activeBinding.customApiId)) {
                setSourceType('custom_api');
                if (activeBinding.customApiId) {
                    setGlobalApiId(activeBinding.customApiId);
                }
            }
        }
    }, [activeBinding]);

    // Default global selections on initial load
    useEffect(() => {
        if (!globalApiId && customApis.length > 0) {
            const firstBound = bindableLayers.find(l => (l.binding?.sourceType === 'custom_api' || !l.binding?.sourceType) && l.binding?.customApiId && !l.binding?.webhookId);
            if (firstBound?.binding?.customApiId) {
                setGlobalApiId(firstBound.binding.customApiId);
            } else if (customApis[0]?._id) {
                setGlobalApiId(customApis[0]._id);
            }
        }
    }, [bindableLayers, globalApiId, customApis]);

    useEffect(() => {
        if (webhooks.length > 0) {
            const isValid = webhooks.some(w => String(w._id) === String(globalWebhookId) || String(w.webhook_id) === String(globalWebhookId));
            if (!globalWebhookId || !isValid) {
                const firstBoundWh = bindableLayers.find(l => l.binding?.sourceType === 'webhook' || Boolean(l.binding?.webhookId));
                if (firstBoundWh) {
                    const boundId = firstBoundWh.binding?.webhookId || firstBoundWh.binding?.customApiId || '';
                    if (webhooks.some(w => String(w._id) === String(boundId) || String(w.webhook_id) === String(boundId))) {
                        setGlobalWebhookId(boundId);
                        return;
                    }
                }
                const defaultWh = webhooks[0];
                setGlobalWebhookId(defaultWh.webhook_id || defaultWh._id);
            }
        }
    }, [bindableLayers, globalWebhookId, webhooks]);

    const activeApiId = activeBindableLayer ? selectedApiId : globalApiId;
    const activeWebhookId = activeBindableLayer ? (selectedWebhookIdFromBinding || globalWebhookId) : globalWebhookId;



    const selectedWebhook: WebhookData | null = useMemo(() => {
        return webhooks.find(w => String(w._id) === String(activeWebhookId) || String(w.webhook_id) === String(activeWebhookId)) || null;
    }, [webhooks, activeWebhookId]);

    // Custom Dynamic Injections State (for manual sample variables)
    const [isAddFormOpen, setIsAddFormOpen] = useState(false);
    const [customKey, setCustomKey] = useState('');
    const [customValue, setCustomValue] = useState('');
    const [injectionsTrigger, setInjectionsTrigger] = useState(0);

    // Load custom variables from localStorage
    const localInjections = useMemo(() => {
        if (!activeApiId) return {};
        try {
            const saved = localStorage.getItem(`wejha_api_injections_${activeApiId}`);
            return saved ? JSON.parse(saved) : {};
        } catch (e) {
            return {};
        }
    }, [activeApiId, injectionsTrigger]);

    const webhookLocalInjections = useMemo(() => {
        if (!activeWebhookId) return {};
        try {
            const saved = localStorage.getItem(`wejha_webhook_injections_${activeWebhookId}`);
            return saved ? JSON.parse(saved) : {};
        } catch (e) {
            return {};
        }
    }, [activeWebhookId, injectionsTrigger]);

    // Selected API object (with local injections)
    const selectedApi = useMemo(() => {
        const api = customApis.find(a => String(a._id) === String(activeApiId));
        if (!api) return null;
        return {
            ...api,
            customapidata: {
                ...(api.customapidata || {}),
                ...localInjections
            }
        };
    }, [customApis, activeApiId, localInjections]);

    // Webhook Payload object (using latest_payload on webhook model and sample injections)
    const webhookPayloadData = useMemo(() => {
        if (!selectedWebhook && !activeWebhookId) return null;
        let payload = selectedWebhook?.latest_payload;
        if (payload === undefined || payload === null) {
            if (Object.keys(webhookLocalInjections).length > 0) {
                return webhookLocalInjections;
            }
            return null;
        }
        if (typeof payload === 'object' && payload !== null) {
            if (Array.isArray(payload)) {
                const merged = [...payload];
                Object.assign(merged, webhookLocalInjections);
                return merged;
            }
            return {
                ...payload,
                ...webhookLocalInjections
            };
        }
        return payload;
    }, [selectedWebhook, activeWebhookId, webhookLocalInjections]);

    // Active source data based on sourceType
    const activeSourceData = sourceType === 'webhook' ? webhookPayloadData : selectedApi?.customapidata;
    const activeKey = sourceType === 'webhook' ? selectedApiKey : selectedApiKey;

    const arrayLength = useMemo(() => {
        return activeSourceData ? getArrayLength(activeSourceData) : 1;
    }, [activeSourceData]);

    // Live value resolution
    const liveValue = useMemo(() => {
        if (!activeSourceData || !activeKey) return null;
        const val = resolveJsonPath(activeSourceData, activeKey);
        return val !== undefined && val !== null ? String(val) : null;
    }, [activeSourceData, activeKey]);

    const handleApiChange = (apiId: string) => {
        if (!activeBindableLayer) {
            setGlobalApiId(apiId === 'none' ? '' : apiId);
            return;
        }
        if (!apiId || apiId === 'none') {
            onBindingChange(activeBindableLayer.id, null);
            return;
        }

        const nextApi = customApis.find(api => String(api._id) === String(apiId));
        const keys = nextApi && nextApi.customapidata ? getFlattenedKeys(nextApi.customapidata) : [];

        let finalKey = '';
        if (autoMapEnabled && activeBindableLayer.name && keys.length > 0) {
            const match = findFuzzyMatch(activeBindableLayer.name, keys, category);
            if (match) finalKey = match;
        }

        onBindingChange(activeBindableLayer.id, {
            sourceType: 'custom_api',
            customApiId: apiId,
            apiKey: finalKey,
            fallbackValue: fallbackValue,
        });
    };

    const handleWebhookChange = (webhookId: string) => {
        if (!activeBindableLayer) {
            setGlobalWebhookId(webhookId === 'none' ? '' : webhookId);
            return;
        }
        if (!webhookId || webhookId === 'none') {
            onBindingChange(activeBindableLayer.id, null);
            return;
        }

        const wh = webhooks.find(w => String(w._id) === String(webhookId) || String(w.webhook_id) === String(webhookId));
        const whPayload = wh?.latest_payload;
        const keys = whPayload ? getFlattenedKeys(whPayload) : [];

        let finalKey = '';
        if (autoMapEnabled && activeBindableLayer.name && keys.length > 0) {
            const match = findFuzzyMatch(activeBindableLayer.name, keys, category);
            if (match) finalKey = match;
        }

        const selectedWhRef = wh?.webhook_id || wh?._id || webhookId;

        onBindingChange(activeBindableLayer.id, {
            sourceType: 'webhook',
            webhookId: selectedWhRef,
            customApiId: selectedWhRef,
            apiKey: finalKey,
            fallbackValue: fallbackValue,
        });
    };

    const handleKeyChange = (key: string) => {
        const targetLayerId = activeBindableLayer?.id;
        if (!targetLayerId) return;

        if (sourceType === 'webhook') {
            const targetWebhookId = activeWebhookId;
            if (!targetWebhookId) return;

            onBindingChange(targetLayerId, {
                sourceType: 'webhook',
                webhookId: targetWebhookId,
                customApiId: targetWebhookId,
                apiKey: key,
                fallbackValue,
            });
        } else {
            const targetApiId = activeBindableLayer ? selectedApiId : globalApiId;
            if (!targetApiId) return;

            onBindingChange(targetLayerId, {
                sourceType: 'custom_api',
                customApiId: targetApiId,
                apiKey: key,
                fallbackValue,
            });
        }
    };

    const handleFallbackChange = (val: string) => {
        if (!activeBindableLayer) return;
        if (sourceType === 'webhook') {
            const targetWhId = activeBinding?.webhookId || activeBinding?.customApiId || activeWebhookId;
            if (!targetWhId || !activeKey) return;
            onBindingChange(activeBindableLayer.id, {
                sourceType: 'webhook',
                webhookId: targetWhId,
                customApiId: targetWhId,
                apiKey: activeKey,
                fallbackValue: val,
            });
        } else {
            if (!selectedApiId || !selectedApiKey) return;
            onBindingChange(activeBindableLayer.id, {
                sourceType: 'custom_api',
                customApiId: selectedApiId,
                apiKey: selectedApiKey,
                fallbackValue: val,
            });
        }
    };

    const handleAddCustomVariable = (key: string, value: string) => {
        if (sourceType === 'webhook') {
            const targetWhId = activeWebhookId;
            if (!targetWhId) return;

            const updated = {
                ...webhookLocalInjections,
                [key]: value
            };
            localStorage.setItem(`wejha_webhook_injections_${targetWhId}`, JSON.stringify(updated));
            setInjectionsTrigger(prev => prev + 1);

            if (activeBindableLayer) {
                onBindingChange(activeBindableLayer.id, {
                    sourceType: 'webhook',
                    webhookId: targetWhId,
                    customApiId: targetWhId,
                    apiKey: key,
                    fallbackValue: value,
                });
            }
            toast.success(`Injected custom variable: "${key}"`);
        } else {
            const targetId = activeBindableLayer ? selectedApiId : globalApiId;
            if (!targetId) return;

            const updated = {
                ...localInjections,
                [key]: value
            };
            localStorage.setItem(`wejha_api_injections_${targetId}`, JSON.stringify(updated));
            setInjectionsTrigger(prev => prev + 1);

            if (activeBindableLayer) {
                onBindingChange(activeBindableLayer.id, {
                    sourceType: 'custom_api',
                    customApiId: targetId,
                    apiKey: key,
                    fallbackValue: value,
                });
            }
            toast.success(`Injected custom variable: "${key}"`);
        }
    };

    const handleUnbind = () => {
        if (!activeBindableLayer) return;
        onBindingChange(activeBindableLayer.id, null);
    };

    // HTML5 Drag Start Handler
    const handleDragStart = (e: React.DragEvent, apiKey: string, value: any) => {
        if (sourceType === 'webhook') {
            const targetWebhookId = activeWebhookId;
            const dragData = {
                type: 'data-binding',
                data: {
                    sourceType: 'webhook',
                    webhookId: targetWebhookId,
                    customApiId: targetWebhookId,
                    apiKey,
                    value: String(value ?? '')
                }
            };
            e.dataTransfer.setData('text/plain', JSON.stringify(dragData));
            e.dataTransfer.effectAllowed = 'copy';

            try {
                const dragIcon = document.createElement('div');
                dragIcon.className = 'px-3 py-1.5 bg-amber-500 text-white text-xs font-bold rounded-lg shadow-lg flex items-center gap-1.5 absolute -top-100 -left-100';
                dragIcon.innerHTML = `⚡ Bind: ${apiKey.split('.').pop()}`;
                document.body.appendChild(dragIcon);
                e.dataTransfer.setDragImage(dragIcon, 10, 10);
                setTimeout(() => document.body.removeChild(dragIcon), 0);
            } catch (err) { /* ignore */ }
        } else {
            const targetApiId = activeBindableLayer ? selectedApiId : globalApiId;
            const dragData = {
                type: 'data-binding',
                data: {
                    sourceType: 'custom_api',
                    customApiId: targetApiId,
                    apiKey,
                    value: String(value ?? '')
                }
            };
            e.dataTransfer.setData('text/plain', JSON.stringify(dragData));
            e.dataTransfer.effectAllowed = 'copy';

            try {
                const dragIcon = document.createElement('div');
                dragIcon.className = 'px-3 py-1.5 bg-[#30A84B] text-white text-xs font-bold rounded-lg shadow-lg flex items-center gap-1.5 absolute -top-100 -left-100';
                dragIcon.innerHTML = `🔌 Bind: ${apiKey.split('.').pop()}`;
                document.body.appendChild(dragIcon);
                e.dataTransfer.setDragImage(dragIcon, 10, 10);
                setTimeout(() => document.body.removeChild(dragIcon), 0);
            } catch (err) { /* ignore */ }
        }
    };

    const handleQuickAction = (key: string, val: any) => {
        if (sourceType === 'webhook') {
            const targetWebhookId = activeWebhookId;
            if (!targetWebhookId) return;

            if (activeBindableLayer) {
                handleKeyChange(key);
            } else if (onAddBoundLayer) {
                onAddBoundLayer(targetWebhookId, key, String(val ?? ''), undefined, undefined, undefined, undefined, false, false, 'webhook', targetWebhookId);
            }
        } else {
            const targetApiId = activeBindableLayer ? selectedApiId : globalApiId;
            if (!targetApiId) return;

            if (activeBindableLayer) {
                handleKeyChange(key);
            } else if (onAddBoundLayer) {
                onAddBoundLayer(targetApiId, key, String(val ?? ''), undefined, undefined, undefined, undefined, false, false, 'custom_api');
            }
        }
    };

    const isGlobalFetching = isCustomApisFetching || isWebhooksFetching;

    const handleGlobalRefetch = () => {
        if (sourceType === 'webhook') {
            refetchWebhooks();
        } else {
            refetchCustomApis();
        }
    };

    const isInitialLoading = sourceType === 'webhook' ? isWebhooksLoading : isCustomApisLoading;

    return (
        <div className="w-[360px] h-full bg-white shadow-2xl flex flex-col border-l border-[#e4e7ec] animate-in slide-in-from-right duration-300">
            {/* Header */}
            <div className="h-[68px] flex items-center justify-between px-5 border-b border-[#e4e7ec] bg-white shrink-0">
                <div className="flex items-center gap-3 overflow-hidden">
                    <div className="w-8 h-8 rounded-lg bg-[#30A84B]/10 flex items-center justify-center shrink-0">
                        {sourceType === 'webhook' ? (
                            <Zap className="w-4 h-4 text-amber-500 fill-amber-500/20" />
                        ) : (
                            <Database className="w-4 h-4 text-[#30A84B]" />
                        )}
                    </div>
                    <div>
                        <h3 className="font-sans font-semibold text-[#1d2739] text-[16px] truncate">
                            {sourceType === 'webhook' ? 'Webhook Data Feed' : 'Dynamic Data Feed'}
                        </h3>
                        <p className="text-[10px] text-[#98a2b3] font-bold uppercase tracking-widest leading-none mt-0.5 font-sans">
                            {activeBindableLayer ? 'Layer Data Pairing' : 'Canvas Bindings Dashboard'}
                        </p>
                    </div>
                </div>
                <div className="flex items-center gap-1">
                    <button
                        type="button"
                        onClick={handleGlobalRefetch}
                        className={`text-[#667185] hover:text-[#1d2739] transition-all p-1.5 rounded-md hover:bg-gray-100 cursor-pointer ${isGlobalFetching ? 'animate-spin text-[#30A84B]' : ''
                            }`}
                        title="Sync with latest DB/Webhook values"
                    >
                        <RefreshCw className="w-4 h-4" />
                    </button>
                    <button
                        type="button"
                        onClick={onClose}
                        className="text-[#667185] hover:text-[#1d2739] transition-colors p-1.5 rounded-md hover:bg-gray-100 cursor-pointer"
                    >
                        <X className="w-5 h-5" />
                    </button>
                </div>
            </div>

            {/* Main Content Area */}
            <div className="flex-1 overflow-y-auto p-5 scrollbar-none bg-white">
                {isInitialLoading ? (
                    <div className="flex flex-col items-center justify-center py-20 gap-3 text-xs text-gray-500 font-sans">
                        <div className="w-6 h-6 border-2 border-t-transparent border-[#30A84B] rounded-full animate-spin"></div>
                        <span>Loading data feeds...</span>
                    </div>
                ) : (
                    <div className="space-y-6">
                        {/* 1. Context Information Mode */}
                        {activeBindableLayer ? (
                            <div className="space-y-4 animate-in fade-in zoom-in-95 duration-200">
                                <button
                                    type="button"
                                    onClick={() => onSelectLayer(null)}
                                    className="flex items-center gap-1.5 text-xs text-gray-500 hover:text-gray-900 transition-colors font-semibold"
                                >
                                    <ArrowLeft className="w-3.5 h-3.5" /> Back to bindings list
                                </button>

                                <div className="p-3 bg-gray-50 rounded-xl border border-gray-200/60 flex items-center justify-between">
                                    <div className="flex items-center gap-2.5 overflow-hidden">
                                        <div className="p-2 bg-white rounded-lg border border-gray-150 text-gray-600 shrink-0">
                                            {activeBindableLayer.type === 'text' ? (
                                                <Type className="w-4 h-4 text-[#30A84B]" />
                                            ) : (
                                                <Image className="w-4 h-4 text-blue-500" />
                                            )}
                                        </div>
                                        <div className="overflow-hidden">
                                            <span className="block text-[10px] font-black text-gray-400 uppercase tracking-wider leading-none">Selected Layer</span>
                                            <span className="block font-sans font-semibold text-gray-800 text-sm truncate mt-1">{activeBindableLayer.name}</span>
                                        </div>
                                    </div>
                                    {activeBinding && (
                                        <button
                                            type="button"
                                            onClick={handleUnbind}
                                            className="text-[11px] font-bold text-red-500 hover:text-red-700 flex items-center gap-1 px-2.5 py-1 bg-red-50 hover:bg-red-100/50 rounded-lg transition-colors cursor-pointer"
                                        >
                                            <Unlink className="w-3 h-3" /> Unbind
                                        </button>
                                    )}
                                </div>
                            </div>
                        ) : (
                            <div className="bg-gradient-to-br from-green-50/40 to-green-50/10 border border-[#30A84B]/15 rounded-xl p-3.5 space-y-2.5 shadow-sm">
                                <div
                                    className="flex items-center justify-between cursor-pointer select-none"
                                    onClick={handleToggleGuide}
                                    title={isGuideOpen ? "Collapse guide" : "Expand guide"}
                                >
                                    <div className="flex items-center gap-2">
                                        <div className="w-5.5 h-5.5 rounded-md bg-[#30A84B]/10 flex items-center justify-center shrink-0">
                                            <Sparkles className="w-3.5 h-3.5 text-[#30A84B]" />
                                        </div>
                                        <h4 className="font-bold text-gray-805 text-xs font-sans">How to Connect Feed</h4>
                                    </div>
                                    <span className="text-[10px] text-gray-400 font-bold uppercase tracking-wider select-none shrink-0">
                                        {isGuideOpen ? 'Hide' : 'Show'}
                                    </span>
                                </div>
                                {isGuideOpen && (
                                    <div className="space-y-2 text-xs text-gray-650 leading-relaxed font-sans border-t border-green-100/50 pt-2.5 animate-in slide-in-from-top-2 duration-200">
                                        <p className="font-semibold text-gray-700">Get your template running with live data in 3 easy steps:</p>
                                        <ul className="space-y-2 pl-1">
                                            <li className="flex gap-2">
                                                <span className="flex items-center justify-center w-4 h-4 rounded-full bg-[#30A84B]/10 text-[#30A84B] font-bold text-[9px] shrink-0 mt-0.5">1</span>
                                                <span><strong>Choose Source Type:</strong> Select Custom API or Webhook below.</span>
                                            </li>
                                            <li className="flex gap-2">
                                                <span className="flex items-center justify-center w-4 h-4 rounded-full bg-[#30A84B]/10 text-[#30A84B] font-bold text-[9px] shrink-0 mt-0.5">2</span>
                                                <span><strong>Select Feed / Webhook:</strong> Pick your registered endpoint to inspect its dynamic payload.</span>
                                            </li>
                                            <li className="flex gap-2">
                                                <span className="flex items-center justify-center w-4 h-4 rounded-full bg-[#30A84B]/10 text-[#30A84B] font-bold text-[9px] shrink-0 mt-0.5">3</span>
                                                <span><strong>Drag & Drop or Click:</strong> Drag variables onto canvas widgets or click a field to bind it to the active layer.</span>
                                            </li>
                                        </ul>
                                    </div>
                                )}
                            </div>
                        )}

                        {/* 2. Global Bindings Info - Show when no layer is selected */}
                        {!activeBindableLayer && bindableLayers.length > 0 && (
                            <div className="space-y-2.5">
                                <div className="flex items-center gap-2">
                                    <h4 className="text-[10px] font-black text-[#98a2b3] uppercase tracking-[0.2em] whitespace-nowrap font-sans">Active Canvas Layers</h4>
                                    <div className="h-px w-full bg-[#f0f2f5]" />
                                </div>
                                <div className="space-y-2 max-h-[160px] overflow-y-auto custom-scrollbar p-0.5">
                                    {bindableLayers.map((layer) => {
                                        const isBound = !!layer.binding;
                                        const isWebhook = layer.binding?.sourceType === 'webhook' || Boolean(layer.binding?.webhookId);
                                        let sourceName = '';
                                        if (isBound) {
                                            if (isWebhook) {
                                                const whId = layer.binding?.webhookId || layer.binding?.customApiId;
                                                const wh = webhooks.find(w => String(w._id) === String(whId) || String(w.webhook_id) === String(whId));
                                                sourceName = wh ? (wh.name || wh.webhook_id) : 'Webhook';
                                            } else {
                                                const boundApi = customApis.find(api => String(api._id) === String(layer.binding?.customApiId));
                                                sourceName = boundApi ? boundApi.name_custom : 'API';
                                            }
                                        }

                                        return (
                                            <div
                                                key={layer.id}
                                                onClick={() => onSelectLayer(layer.id)}
                                                className="p-2.5 bg-white hover:bg-gray-50/60 rounded-xl border border-gray-200 hover:border-gray-300 hover:shadow-sm transition-all duration-200 cursor-pointer flex items-center justify-between group"
                                            >
                                                <div className="flex items-center gap-3 overflow-hidden">
                                                    <div className="p-2 bg-gray-50 rounded-lg border border-gray-100 group-hover:bg-white transition-colors shrink-0 text-gray-500">
                                                        {layer.type === 'text' ? (
                                                            <Type className="w-3.5 h-3.5 text-[#30A84B]/80" />
                                                        ) : (
                                                            <Image className="w-3.5 h-3.5 text-blue-500/80" />
                                                        )}
                                                    </div>
                                                    <div className="overflow-hidden">
                                                        <span className="block font-sans font-semibold text-gray-800 text-xs truncate">{layer.name}</span>
                                                        <span className="block text-[9px] text-gray-400 font-medium leading-none mt-1 truncate">
                                                            {isBound ? (
                                                                <span className="text-[#30A84B] font-semibold flex items-center gap-1 truncate">
                                                                    <CheckCircle2 className="w-2.5 h-2.5 text-[#30A84B] shrink-0" />
                                                                    {isWebhook ? '⚡ ' : ''}{sourceName} ➜ {layer.binding?.apiKey?.split('.').pop() || 'Field'}
                                                                </span>
                                                            ) : (
                                                                'Static Content (No binding)'
                                                            )}
                                                        </span>
                                                    </div>
                                                </div>
                                                <div className="w-5 h-5 rounded-full bg-gray-50 group-hover:bg-[#30A84B]/10 flex items-center justify-center shrink-0 text-gray-455 group-hover:text-[#30A84B] transition-all text-xs">
                                                    ➜
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>
                        )}

                        {/* 3. Data Source Selection (Segmented Control + Dropdown) */}
                        <div className="space-y-4">
                            <div className="flex items-center gap-2">
                                <h4 className="text-[10px] font-black text-[#98a2b3] uppercase tracking-[0.2em] whitespace-nowrap font-sans">Data Source</h4>
                                <div className="h-px w-full bg-[#f0f2f5]" />
                            </div>

                            {/* Source Type Selector */}
                            <div className="grid grid-cols-2 p-1 bg-gray-100 rounded-lg text-xs font-semibold">
                                <button
                                    type="button"
                                    onClick={() => setSourceType('custom_api')}
                                    className={`flex items-center justify-center gap-1.5 py-1.5 px-3 rounded-md transition-all cursor-pointer ${sourceType === 'custom_api'
                                            ? 'bg-white text-gray-900 shadow-sm font-bold'
                                            : 'text-gray-500 hover:text-gray-800'
                                        }`}
                                >
                                    <Globe className="w-3.5 h-3.5 text-[#30A84B]" />
                                    <span>Custom API</span>
                                </button>
                                <button
                                    type="button"
                                    onClick={() => setSourceType('webhook')}
                                    className={`flex items-center justify-center gap-1.5 py-1.5 px-3 rounded-md transition-all cursor-pointer ${sourceType === 'webhook'
                                            ? 'bg-white text-gray-900 shadow-sm font-bold'
                                            : 'text-gray-500 hover:text-gray-800'
                                        }`}
                                >
                                    <Zap className="w-3.5 h-3.5 text-amber-500 fill-amber-500/20" />
                                    <span>Webhook</span>
                                </button>
                            </div>

                            {/* Dropdown for Custom API */}
                            {sourceType === 'custom_api' ? (
                                <div className="space-y-1.5">
                                    <label className="block text-[11px] font-semibold text-gray-650">
                                        Integration Source
                                    </label>
                                    {customApis.length === 0 ? (
                                        <div className="p-3 bg-gray-50 rounded-xl border border-gray-200 text-center font-sans space-y-2">
                                            <p className="text-xs text-gray-500">No Custom APIs connected.</p>
                                            <a
                                                href="/integrations"
                                                target="_blank"
                                                className="inline-block px-3 py-1.5 border border-[#30A84B] text-[#30A84B] rounded-lg font-bold text-xs hover:bg-[#30A84B]/5 transition-colors"
                                            >
                                                Configure integrations →
                                            </a>
                                        </div>
                                    ) : (
                                        <Select
                                            value={activeBindableLayer
                                                ? ((activeBinding?.sourceType !== 'webhook' && !activeBinding?.webhookId && selectedApiId) ? String(selectedApiId) : 'none')
                                                : (globalApiId ? String(globalApiId) : 'none')}
                                            onValueChange={handleApiChange}
                                        >
                                            <SelectTrigger className="w-full h-10 px-3 border border-gray-250 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#30A84B]/10 focus:border-[#30A84B] bg-white text-sm text-gray-700 font-sans shadow-none">
                                                <SelectValue placeholder="Select integration..." />
                                            </SelectTrigger>
                                            <SelectContent className="bg-white border border-gray-200 shadow-2xl rounded-lg z-[99999] max-h-56">
                                                <SelectItem value="none" className="text-sm py-2 cursor-pointer focus:bg-[#30A84B]/5 focus:text-[#30A84B]">
                                                    Select an integration...
                                                </SelectItem>
                                                {filteredApis.map((api) => {
                                                    const isMatch = api.api_type === category.toLowerCase();
                                                    const apiIdStr = api._id ? String(api._id) : '';
                                                    return (
                                                        <SelectItem key={apiIdStr} value={apiIdStr} className="text-sm py-2 cursor-pointer focus:bg-[#30A84B]/5 focus:text-[#30A84B]">
                                                            {api.name_custom} {isMatch ? '⭐' : ''}
                                                        </SelectItem>
                                                    );
                                                })}
                                            </SelectContent>
                                        </Select>
                                    )}
                                </div>
                            ) : (
                                /* Dropdown for Webhooks */
                                <div className="space-y-1.5">
                                    <label className="block text-[11px] font-semibold text-gray-650">
                                        Webhook Source
                                    </label>
                                    {webhooks.length === 0 ? (
                                        <div className="p-3 bg-gray-50 rounded-xl border border-gray-200 text-center font-sans space-y-2">
                                            <p className="text-xs text-gray-600 font-medium">No Webhooks registered yet.</p>
                                            <a
                                                href="/integrations"
                                                target="_blank"
                                                className="inline-block px-3 py-1.5 border border-amber-500 text-amber-600 rounded-lg font-bold text-xs hover:bg-amber-50 transition-colors"
                                            >
                                                Configure Webhooks →
                                            </a>
                                        </div>
                                    ) : (
                                        <Select
                                            value={activeBindableLayer
                                                ? ((activeBinding?.sourceType === 'webhook' || activeBinding?.webhookId) ? String(activeWebhookId || 'none') : 'none')
                                                : (globalWebhookId ? String(globalWebhookId) : 'none')}
                                            onValueChange={handleWebhookChange}
                                        >
                                            <SelectTrigger className="w-full h-10 px-3 border border-gray-250 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500/10 focus:border-amber-500 bg-white text-sm text-gray-700 font-sans shadow-none">
                                                <SelectValue placeholder="Select a webhook..." />
                                            </SelectTrigger>
                                            <SelectContent className="bg-white border border-gray-200 shadow-2xl rounded-lg z-[99999] max-h-56">
                                                <SelectItem value="none" className="text-sm py-2 cursor-pointer focus:bg-amber-50 focus:text-amber-800">
                                                    Select a webhook...
                                                </SelectItem>
                                                {webhooks.map((wh) => {
                                                    const whIdStr = wh.webhook_id || wh._id;
                                                    return (
                                                        <SelectItem key={whIdStr} value={whIdStr} className="text-sm py-2 cursor-pointer focus:bg-amber-50 focus:text-amber-800">
                                                            {wh.name || wh.webhook_id}
                                                        </SelectItem>
                                                    );
                                                })}
                                            </SelectContent>
                                        </Select>
                                    )}

                                    {selectedWebhook && (
                                        <div className="flex items-center justify-between text-[10px] text-gray-400 px-0.5 pt-0.5 font-sans">
                                            <span className="font-mono truncate max-w-[170px]" title={selectedWebhook.webhook_id}>
                                                ID: {selectedWebhook.webhook_id}
                                            </span>
                                            {selectedWebhook.last_received_at && (
                                                <span title={selectedWebhook.last_received_at}>
                                                    Updated {new Date(selectedWebhook.last_received_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                                                </span>
                                            )}
                                        </div>
                                    )}
                                </div>
                            )}

                            {/* Add Custom Variable Form (for testing custom payload keys) */}
                            {((sourceType === 'custom_api' && ((activeBindableLayer && selectedApiId) || (!activeBindableLayer && globalApiId))) ||
                                (sourceType === 'webhook' && activeWebhookId)) && (
                                    <div className="p-3 bg-gray-50/50 rounded-xl border border-gray-200/60 space-y-2.5">
                                        <div className="flex items-center justify-between cursor-pointer select-none" onClick={() => setIsAddFormOpen(!isAddFormOpen)}>
                                            <span className="text-xs font-bold text-gray-700 flex items-center gap-1.5">
                                                <Plus className="w-3.5 h-3.5 text-[#30A84B]" />
                                                Add Custom Variable
                                            </span>
                                            <span className="text-[10px] text-gray-400 font-semibold">{isAddFormOpen ? 'Collapse' : 'Expand'}</span>
                                        </div>
                                        {isAddFormOpen && (
                                            <div className="space-y-2 animate-in slide-in-from-top-2 duration-200">
                                                <div className="space-y-1">
                                                    <label className="text-[9px] font-bold text-gray-450 uppercase tracking-wider block">Variable Key Name</label>
                                                    <input
                                                        type="text"
                                                        placeholder="e.g. alert.message"
                                                        value={customKey}
                                                        onChange={(e) => setCustomKey(e.target.value.replace(/[^a-zA-Z0-9_.]/g, ''))}
                                                        className="w-full px-2.5 py-1.5 border border-gray-200 rounded-lg text-xs font-sans outline-none focus:border-[#30A84B] focus:ring-1 focus:ring-[#30A84B] bg-white text-gray-700"
                                                    />
                                                </div>
                                                <div className="space-y-1">
                                                    <label className="text-[9px] font-bold text-gray-450 uppercase tracking-wider block">Sample Value</label>
                                                    <input
                                                        type="text"
                                                        placeholder="e.g. Critical weather warning"
                                                        value={customValue}
                                                        onChange={(e) => setCustomValue(e.target.value)}
                                                        className="w-full px-2.5 py-1.5 border border-gray-200 rounded-lg text-xs font-sans outline-none focus:border-[#30A84B] focus:ring-1 focus:ring-[#30A84B] bg-white text-gray-700"
                                                    />
                                                </div>
                                                <button
                                                    type="button"
                                                    onClick={() => {
                                                        if (!customKey.trim() || !customValue.trim()) return;
                                                        handleAddCustomVariable(customKey.trim(), customValue.trim());
                                                        setCustomKey('');
                                                        setCustomValue('');
                                                    }}
                                                    disabled={!customKey.trim() || !customValue.trim()}
                                                    className="w-full py-1.5 bg-[#30A84B] hover:bg-[#25873a] disabled:opacity-50 text-white rounded-lg text-xs font-bold transition-all cursor-pointer flex items-center justify-center gap-1 shadow-sm"
                                                >
                                                    <Plus className="w-3.5 h-3.5" />
                                                    Add Variable
                                                </button>
                                            </div>
                                        )}
                                    </div>
                                )}

                            {/* Dynamic Toggles and Auto-mapping */}
                            {((sourceType === 'custom_api' && ((activeBindableLayer && selectedApiId) || (!activeBindableLayer && globalApiId))) ||
                                (sourceType === 'webhook' && activeWebhookId)) && (
                                    <div className="space-y-3">
                                        {/* Placeholder Mode Toggle */}
                                        <div className="flex items-center justify-between p-3.5 bg-green-50/20 rounded-xl border border-green-100 select-none">
                                            <div className="space-y-0.5">
                                                <span className="block text-xs font-bold text-gray-800 flex items-center gap-1.5">
                                                    {showPlaceholders ? <Eye className="w-3.5 h-3.5 text-[#30A84B]" /> : <EyeOff className="w-3.5 h-3.5 text-gray-400" />}
                                                    Show Placeholders
                                                </span>
                                                <span className="block text-[10px] text-gray-400">Display template tags on canvas</span>
                                            </div>
                                            <label className="relative inline-flex items-center cursor-pointer select-none">
                                                <input
                                                    type="checkbox"
                                                    checked={showPlaceholders}
                                                    onChange={(e) => onShowPlaceholdersChange(e.target.checked)}
                                                    className="sr-only peer"
                                                />
                                                <div className="w-9 h-5 bg-gray-200 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:bg-[#30A84B]"></div>
                                            </label>
                                        </div>

                                        {/* Auto-Generate Pages in Preview Toggle with Settings */}
                                        <div className="flex flex-col p-3.5 bg-blue-50/10 rounded-xl border border-blue-100 select-none space-y-3">
                                            <div className="flex items-center justify-between">
                                                <div className="space-y-0.5">
                                                    <span className="block text-xs font-bold text-gray-800 flex items-center gap-1.5">
                                                        <Layers className="w-3.5 h-3.5 text-blue-500" />
                                                        Auto-Generate Pages in Preview
                                                    </span>
                                                    <span className="block text-[10px] text-gray-400">Generate dynamic pages on preview</span>
                                                </div>
                                                <label className="relative inline-flex items-center cursor-pointer select-none">
                                                    <input
                                                        type="checkbox"
                                                        checked={autoPaginate}
                                                        onChange={(e) => onAutoPaginateChange?.(e.target.checked)}
                                                        className="sr-only peer"
                                                    />
                                                    <div className="w-9 h-5 bg-gray-200 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:bg-blue-500"></div>
                                                </label>
                                            </div>

                                            <div className="border-t border-blue-100/50 pt-3 space-y-3">
                                                {autoPaginate ? (
                                                    <div className="grid grid-cols-2 gap-2">
                                                        <div className="space-y-1">
                                                            <label className="text-[10px] font-bold text-gray-500 block">Start Page</label>
                                                            <input
                                                                type="number"
                                                                min="1"
                                                                value={templateSettings?.startIndex || 1}
                                                                onChange={(e) => onSettingsChange?.({ startIndex: Math.max(1, parseInt(e.target.value) || 1) })}
                                                                className="w-full px-2.5 py-1 border border-gray-250 rounded-lg text-xs bg-white text-gray-705 outline-none focus:border-blue-500"
                                                            />
                                                        </div>
                                                        <div className="space-y-1">
                                                            <label className="text-[10px] font-bold text-gray-500 block">End Page Limit</label>
                                                            <Select
                                                                value={String(templateSettings?.endIndex || 5)}
                                                                onValueChange={(val) => {
                                                                    const numVal = val === 'array.length' ? 'array.length' : parseInt(val);
                                                                    onSettingsChange?.({ endIndex: numVal });
                                                                }}
                                                            >
                                                                <SelectTrigger className="w-full h-8 px-2.5 border border-gray-250 rounded-lg text-xs bg-white text-gray-705 outline-none focus:border-blue-500 select-none shadow-none">
                                                                    <SelectValue placeholder="Select limit..." />
                                                                </SelectTrigger>
                                                                <SelectContent className="bg-white border border-gray-250 shadow-xl rounded-lg z-[99999]">
                                                                    {arrayLength <= 1 ? (
                                                                        <SelectItem value="1" className="text-xs">1 Page</SelectItem>
                                                                    ) : (
                                                                        <>
                                                                            {arrayLength >= 2 && <SelectItem value="2" className="text-xs">2 Pages</SelectItem>}
                                                                            {arrayLength >= 5 && <SelectItem value="5" className="text-xs">5 Pages</SelectItem>}
                                                                            {arrayLength >= 10 && <SelectItem value="10" className="text-xs">10 Pages</SelectItem>}
                                                                            {arrayLength >= 20 && <SelectItem value="20" className="text-xs">20 Pages</SelectItem>}
                                                                            {![2, 5, 10, 20].includes(arrayLength) && (
                                                                                <SelectItem value={String(arrayLength)} className="text-xs">{arrayLength} Pages</SelectItem>
                                                                            )}
                                                                        </>
                                                                    )}
                                                                    <SelectItem value="array.length" className="text-xs font-mono font-bold text-blue-600">n = array.length ({arrayLength})</SelectItem>
                                                                </SelectContent>
                                                            </Select>
                                                        </div>
                                                    </div>
                                                ) : (
                                                    <div className="space-y-1">
                                                        <label className="text-[10px] font-bold text-gray-500 block">Single Item Selection</label>
                                                        <Select
                                                            value={String(templateSettings?.singleItemIndex || 1)}
                                                            onValueChange={(val) => {
                                                                const numVal = val === 'array.length' ? 'array.length' : parseInt(val);
                                                                onSettingsChange?.({ singleItemIndex: numVal });
                                                            }}
                                                        >
                                                            <SelectTrigger className="w-full h-8 px-2.5 border border-gray-250 rounded-lg text-xs bg-white text-gray-705 outline-none focus:border-blue-500 select-none shadow-none">
                                                                <SelectValue placeholder="Select index..." />
                                                            </SelectTrigger>
                                                            <SelectContent className="bg-white border border-gray-250 shadow-xl rounded-lg z-[99999] max-h-56">
                                                                {Array.from({ length: arrayLength }).map((_, idx) => {
                                                                    const itemNumber = idx + 1;
                                                                    const getOrdinalSuffix = (i: number) => {
                                                                        const j = i % 10, k = i % 100;
                                                                        if (j === 1 && k !== 11) return `${i}st`;
                                                                        if (j === 2 && k !== 12) return `${i}nd`;
                                                                        if (j === 3 && k !== 13) return `${i}rd`;
                                                                        return `${i}th`;
                                                                    };
                                                                    return (
                                                                        <SelectItem key={itemNumber} value={String(itemNumber)} className="text-xs">
                                                                            {getOrdinalSuffix(itemNumber)} Item [{idx}]
                                                                        </SelectItem>
                                                                    );
                                                                })}
                                                                <SelectItem value="array.length" className="text-xs font-mono font-bold text-blue-600">n = array.length ({arrayLength})</SelectItem>
                                                            </SelectContent>
                                                        </Select>
                                                    </div>
                                                )}

                                                <div className="space-y-1">
                                                    <label className="text-[10px] font-bold text-gray-500 block">Interval Between Pages (sec)</label>
                                                    <input
                                                        type="number"
                                                        min="1"
                                                        value={templateSettings?.pageInterval || 5}
                                                        onChange={(e) => onSettingsChange?.({ pageInterval: Math.max(1, parseInt(e.target.value) || 1) })}
                                                        className="w-full px-2.5 py-1 border border-gray-250 rounded-lg text-xs bg-white text-gray-705 outline-none focus:border-blue-500"
                                                    />
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                )}
                        </div>

                        {/* 4. Display Views and View Switcher */}
                        {((sourceType === 'custom_api' && ((activeBindableLayer && selectedApiId) || (!activeBindableLayer && globalApiId))) ||
                            (sourceType === 'webhook' && activeWebhookId)) && (
                                <div className="space-y-4 animate-in fade-in duration-200">
                                    {/* Section Divider */}
                                    <div className="flex items-center gap-2">
                                        <h4 className="text-[10px] font-black text-[#98a2b3] uppercase tracking-[0.2em] whitespace-nowrap font-sans">
                                            {sourceType === 'webhook' ? 'Webhook Payload Fields' : 'Variable Mapping'}
                                        </h4>
                                        <div className="h-px w-full bg-[#f0f2f5]" />
                                    </div>

                                    {/* View Switcher Tabs */}
                                    <div className="grid grid-cols-2 gap-1 p-1 bg-gray-50 rounded-xl border border-gray-200">
                                        <button
                                            type="button"
                                            onClick={() => setViewMode('structure')}
                                            className={`flex items-center justify-center gap-1.5 py-1.5 rounded-lg text-xs font-bold transition-all cursor-pointer ${viewMode === 'structure' ? 'bg-white shadow-sm text-gray-900' : 'text-gray-500 hover:text-gray-800'
                                                }`}
                                        >
                                            <Layers className="w-3.5 h-3.5 text-blue-500" />
                                            <span>Structure</span>
                                        </button>
                                        <button
                                            type="button"
                                            onClick={() => setViewMode('json')}
                                            className={`flex items-center justify-center gap-1.5 py-1.5 rounded-lg text-xs font-bold transition-all cursor-pointer ${viewMode === 'json' ? 'bg-white shadow-sm text-gray-900' : 'text-gray-500 hover:text-gray-800'
                                                }`}
                                        >
                                            <Braces className="w-3.5 h-3.5 text-blue-500" />
                                            <span>Current JSON</span>
                                        </button>
                                    </div>

                                    {/* Content Containers */}
                                    <div className="border border-gray-200 rounded-xl overflow-hidden max-h-[400px] overflow-y-auto bg-gray-50/30 p-2 custom-scrollbar">
                                        {activeSourceData ? (
                                            <>
                                                {/* Structure Tab (Interactive JSON Tree) */}
                                                {viewMode === 'structure' && (
                                                    <div className="space-y-1.5 py-1 px-1">
                                                        <JSONNode
                                                            name="root"
                                                            value={activeSourceData}
                                                            path=""
                                                            depth={0}
                                                            selectedApiKey={activeKey}
                                                            onSelectKey={handleKeyChange}
                                                            onDragStart={handleDragStart}
                                                            onAdd={handleQuickAction}
                                                            isWebhook={sourceType === 'webhook'}
                                                        />
                                                    </div>
                                                )}

                                                {/* Current JSON Tab (Raw JSON viewer) */}
                                                {viewMode === 'json' && (
                                                    <pre className="font-mono text-[10px] bg-gray-900 text-green-400 p-3 rounded-lg overflow-x-auto max-h-[380px] whitespace-pre select-text">
                                                        {JSON.stringify(activeSourceData, null, 2)}
                                                    </pre>
                                                )}
                                            </>
                                        ) : sourceType === 'webhook' ? (
                                            <div className="bg-amber-50 border border-amber-200/80 rounded-xl p-3.5 space-y-2 text-xs text-amber-800 leading-normal">
                                                <div className="flex items-center gap-1.5 font-bold text-amber-800">
                                                    <AlertCircle className="w-4 h-4 text-amber-600 shrink-0" />
                                                    <span>No payload received yet</span>
                                                </div>
                                                <p className="text-amber-700 leading-relaxed text-[11px]">
                                                    This webhook has not received external data yet. Send an event to its endpoint to see live keys, or use "Add Custom Variable" above to create sample fields.
                                                </p>
                                            </div>
                                        ) : (
                                            <div className="bg-amber-50 border border-amber-100 rounded-xl p-3 text-xs text-amber-800 leading-normal">
                                                This integration has no cached data. Test it first in the Integrations dashboard to enable path variables.
                                            </div>
                                        )}
                                    </div>
                                </div>
                            )}

                        {/* 5. Live Value Card */}
                        {((sourceType === 'custom_api' && selectedApiId && selectedApiKey) ||
                            (sourceType === 'webhook' && activeWebhookId && activeKey)) && (
                                <div className="space-y-3">
                                    <div className="flex items-center gap-2">
                                        <h4 className="text-[10px] font-black text-[#98a2b3] uppercase tracking-[0.2em] whitespace-nowrap font-sans">Response Preview</h4>
                                        <div className="h-px w-full bg-[#f0f2f5]" />
                                    </div>

                                    <div className={`border rounded-xl p-4 space-y-3.5 ${sourceType === 'webhook' ? 'bg-amber-50/20 border-amber-200/40' : 'bg-[#30A84B]/5 border-[#30A84B]/20'
                                        }`}>
                                        <div className="flex items-center justify-between">
                                            <span className={`text-[10px] font-black uppercase tracking-wider ${sourceType === 'webhook' ? 'text-amber-700' : 'text-[#30A84B]'
                                                }`}>
                                                Resolved value
                                            </span>
                                            <span className={`text-[9px] px-2.5 py-0.5 rounded-full font-bold ${sourceType === 'webhook' ? 'bg-amber-100 text-amber-800' : 'bg-[#30A84B]/10 text-[#30A84B]'
                                                }`}>
                                                Active
                                            </span>
                                        </div>
                                        <div className="space-y-2">
                                            <div className="text-[13px] font-semibold text-gray-800 break-all leading-relaxed bg-white p-3 rounded-lg border border-gray-200/70 shadow-sm max-h-32 overflow-y-auto">
                                                {liveValue !== null ? (
                                                    (liveValue.startsWith('http') && /\.(jpg|jpeg|png|webp|gif|svg)/i.test(liveValue.split('?')[0])) ? (
                                                        <div className="space-y-2">
                                                            <img src={liveValue} alt="Live preview" className="h-16 w-auto rounded border border-gray-200 object-contain mx-auto" />
                                                            <span className="text-[10px] text-gray-400 block truncate text-center">{liveValue}</span>
                                                        </div>
                                                    ) : (
                                                        liveValue
                                                    )
                                                ) : (
                                                    <span className="text-amber-600 italic flex items-center gap-1.5 font-semibold">
                                                        <AlertCircle className="w-4 h-4 text-amber-500 shrink-0" /> Using fallback (no data resolved)
                                                    </span>
                                                )}
                                            </div>
                                            <div className="text-[10px] text-gray-400 leading-normal flex gap-1">
                                                <span>Path:</span>
                                                <code className="bg-gray-100 px-1 py-0.5 rounded text-gray-650 font-mono text-[10px] truncate max-w-[200px]" title={activeKey}>{activeKey}</code>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            )}

                        {/* Fallback Value Input */}
                        {activeBindableLayer && ((sourceType === 'custom_api' && selectedApiId && selectedApiKey) || (sourceType === 'webhook' && activeWebhookId && activeKey)) && (
                            <div className="space-y-1.5 pt-1">
                                <label className="block text-[11px] font-semibold text-gray-600">
                                    Fallback Value
                                </label>
                                <input
                                    type="text"
                                    placeholder="Fallback if API/Webhook is unavailable"
                                    value={fallbackValue}
                                    onChange={(e) => handleFallbackChange(e.target.value)}
                                    className="w-full px-3 py-2 border border-gray-250 rounded-lg focus:outline-none focus:border-[#30A84B] focus:ring-2 focus:ring-[#30A84B]/10 text-xs text-gray-700 font-sans transition-all"
                                />
                            </div>
                        )}

                        {/* Warnings if not mapped */}
                        {activeBindableLayer && ((sourceType === 'custom_api' && selectedApiId && !selectedApiKey) || (sourceType === 'webhook' && activeWebhookId && !activeKey)) && (
                            <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 space-y-2.5 animate-in fade-in duration-200">
                                <div className="flex items-center gap-1.5 text-amber-800 font-bold text-[10px] uppercase tracking-wider">
                                    <AlertCircle className="w-4 h-4 text-amber-600 shrink-0" />
                                    <span>Not Mapped</span>
                                </div>
                                <p className="text-xs text-amber-700 leading-relaxed">
                                    Please select a variable path or node from the list above to bind this layer's content.
                                </p>
                            </div>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
};
